import { Capacitor } from '@capacitor/core';

/** The store a purchase was billed through, as analytics reports it. */
export type PurchaseStore = 'app_store' | 'play_store' | 'stripe' | 'paypal';

export function getNativeStoreForPlatform(
  platform = Capacitor.getPlatform()
): 'app_store' | 'play_store' | null {
  if (platform === 'ios') return 'app_store';
  if (platform === 'android') return 'play_store';
  return null;
}

/**
 * Maps a billing provider or a RevenueCat store name (`APP_STORE`,
 * `MAC_APP_STORE`, `PLAY_STORE`, `STRIPE`) to one lowercase store value.
 * Anything else (promotional grants, unknown stores, `native_store`) is null.
 */
export function normalizePurchaseStore(value: unknown): PurchaseStore | null {
  if (typeof value !== 'string') return null;
  switch (value.trim().toLowerCase()) {
    case 'app_store':
    case 'mac_app_store':
      return 'app_store';
    case 'play_store':
      return 'play_store';
    case 'stripe':
      return 'stripe';
    case 'paypal':
      return 'paypal';
    default:
      return null;
  }
}

/**
 * Store for a purchase event: the provider or RevenueCat value when it names
 * a store, else the store of the native platform this runs on (a purchase
 * made in the iOS app is an App Store purchase even when RevenueCat has not
 * reported its entitlement yet). Null on web without a known provider.
 */
export function resolvePurchaseStore(
  value: unknown,
  platform = Capacitor.getPlatform()
): PurchaseStore | null {
  return normalizePurchaseStore(value) ?? getNativeStoreForPlatform(platform);
}

export function getNativeTrackingContext(): {
  is_native: boolean;
  platform: string;
  native_platform: string | null;
  store: string | null;
  provider: 'native_store' | null;
} {
  const isNative = Capacitor.isNativePlatform();
  const platform = Capacitor.getPlatform();

  return {
    is_native: isNative,
    platform,
    native_platform: isNative ? platform : null,
    store: isNative ? getNativeStoreForPlatform(platform) : null,
    provider: isNative ? 'native_store' : null,
  };
}
